import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Newspaper, CalendarDays, MapPin, Link as LinkIcon, ExternalLink, ChevronUp, ChevronDown, X, Image as ImageIcon } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const NE_MAX_PHOTOS = 5;

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  title: "",
  item_type: "News",
  event_date: "",
  location: "",
  cover_url: "",
  cover_public_id: "",
  description: "",
  order: 0,
  status: "Active",
  photos: [],
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; }
}

export default function NewsEvents() {
  const { role } = useAuth();
  const canEdit = ["super_admin", "editor"].includes(role);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/news-events");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...emptyForm, order: items.length }); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id, title: p.title || "", item_type: p.item_type || "News",
      event_date: (p.event_date || "").slice(0, 10), location: p.location || "",
      cover_url: p.cover_url || "", cover_public_id: p.cover_public_id || "",
      description: p.description || "", order: p.order || 0,
      status: p.status || "Active",
      photos: Array.isArray(p.photos) ? p.photos.map((ph, i) => ({
        url: ph.url || "", public_id: ph.public_id || "",
        caption: ph.caption || "", order: i,
      })) : [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!["News", "Event"].includes(form.item_type)) return toast.error("Type must be News or Event");
    const cleanedPhotos = (form.photos || [])
      .filter((p) => (p.url || "").trim())
      .slice(0, NE_MAX_PHOTOS)
      .map((p, i) => ({
        url: p.url, public_id: p.public_id || "",
        caption: (p.caption || "").trim().slice(0, 200), order: i,
      }));
    const payload = {
      title: form.title.trim(),
      item_type: form.item_type,
      event_date: (form.event_date || "").slice(0, 10),
      location: (form.location || "").trim(),
      cover_url: form.cover_url || "",
      cover_public_id: form.cover_public_id || "",
      description: (form.description || "").trim(),
      order: Number(form.order) || 0,
      status: form.status === "Inactive" ? "Inactive" : "Active",
      photos: cleanedPhotos,
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/news-events/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/news-events", payload);
        toast.success("Added");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/news-events/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const visible = filter === "All" ? items : items.filter((i) => i.item_type === filter);

  return (
    <div className="space-y-6" data-testid="admin-news-events">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">News &amp; Events</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Announcements and upcoming events shown on the public home page.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="adm-news-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
            <Plus size={16} /> Add Item
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {["All", "News", "Event"].map((t) => (
          <button key={t} type="button" onClick={() => setFilter(t)} data-testid={`adm-news-filter-${t}`} className={`text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${filter === t ? "bg-[#234333] text-white border-[#234333]" : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#f5f1e8]"}`}>
            {t} ({t === "All" ? items.length : items.filter((i) => i.item_type === t).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <Newspaper size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No items yet.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((p) => (
            <div key={p.id} data-testid={`adm-news-card-${p.id}`} className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col">
              <div className="aspect-video bg-[#f5f1e8] overflow-hidden relative">
                {p.cover_url ? <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full flex items-center justify-center text-[#bcb6aa]"><Newspaper size={32} /></div>
                )}
                <div className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold ${p.item_type === "Event" ? "bg-amber-500 text-white" : "bg-[#234333] text-white"}`}>
                  {p.item_type}
                </div>
                {p.status !== "Active" && <div className="absolute top-2 right-2 bg-red-100 text-red-700 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold">Inactive</div>}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-semibold text-[#1c1a17] leading-snug text-sm">{p.title}</div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-[#6b655b]">
                  {p.event_date && <span className="inline-flex items-center gap-1"><CalendarDays size={11} />{fmtDate(p.event_date)}</span>}
                  {p.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{p.location}</span>}
                </div>
                {p.description && <div className="mt-2 text-xs text-[#5b5650] line-clamp-3">{p.description.replace(/<[^>]*>/g, "")}</div>}
                {canEdit && (
                  <div className="mt-auto pt-3 flex items-center gap-1.5">
                    <button type="button" onClick={() => openEdit(p)} data-testid={`adm-news-edit-${p.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} />Edit</button>
                    <button type="button" onClick={() => setConfirmDelete(p)} data-testid={`adm-news-delete-${p.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm"><Trash2 size={11} />Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Item" : "Add News / Event"}</DialogTitle>
            <DialogDescription>Add an announcement or upcoming event. Cover photo is shown on the home page slider.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *" full>
              <input data-testid="adm-news-input-title" type="text" maxLength={200} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Type *">
              <select data-testid="adm-news-input-type" className={inputCls} value={form.item_type} onChange={(e) => set("item_type", e.target.value)}>
                <option value="News">News</option>
                <option value="Event">Event</option>
              </select>
            </Field>
            <Field label="Date">
              <input data-testid="adm-news-input-date" type="date" className={inputCls} value={form.event_date} onChange={(e) => set("event_date", e.target.value)} />
            </Field>
            <Field label="Location">
              <input data-testid="adm-news-input-location" type="text" maxLength={160} className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Optional" />
            </Field>
            <Field label="Status">
              <select data-testid="adm-news-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Cover Photo (200 KB max)" full>
              <FileUploader kind="photo" label="cover photo" url={form.cover_url} onChange={({ url, public_id }) => { set("cover_url", url || ""); set("cover_public_id", public_id || ""); }} testid="adm-news-cover" />
            </Field>
            <Field label={`Additional Photos (up to ${NE_MAX_PHOTOS}, max 750 KB each)`} full>
              <PhotosManager
                photos={form.photos}
                onChange={(next) => set("photos", next)}
              />
            </Field>
            <Field label="Description" full>
              <textarea data-testid="adm-news-input-desc" rows={5} maxLength={8000} className={`${inputCls} resize-y`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Brief details" />
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="adm-news-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Add")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete?</AlertDialogTitle>
            <AlertDialogDescription>“{confirmDelete?.title}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-news-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportantLinksPanel canEdit={canEdit} />
    </div>
  );
}

// -------------------------------------------------------------
// PhotosManager — up to 5 supplementary photos per News/Event.
// Each photo (≤ 750 KB, server-enforced) can carry an optional
// caption and be reordered via up/down arrows.
// -------------------------------------------------------------
function PhotosManager({ photos, onChange }) {
  const list = photos || [];
  const setAt = (idx, patch) => {
    const next = list.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange(next);
  };
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const addSlot = () => {
    if (list.length >= NE_MAX_PHOTOS) return;
    onChange([...list, { url: "", public_id: "", caption: "", order: list.length }]);
  };

  return (
    <div data-testid="adm-news-photos">
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((p, i) => (
          <div
            key={i}
            data-testid={`adm-news-photo-slot-${i}`}
            className="bg-[#faf8f2] border border-[#e5e0d8] rounded-sm p-3 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                Photo {i + 1}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                  data-testid={`adm-news-photo-up-${i}`}
                  className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, +1)}
                  disabled={i === list.length - 1}
                  aria-label="Move down"
                  data-testid={`adm-news-photo-down-${i}`}
                  className="text-[#6b655b] hover:text-[#234333] disabled:opacity-30 p-1"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Remove"
                  data-testid={`adm-news-photo-remove-${i}`}
                  className="text-red-700 hover:text-red-900 p-1"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            <FileUploader
              kind="news_event_photo"
              label=""
              url={p.url}
              onChange={({ url, public_id }) => setAt(i, { url: url || "", public_id: public_id || "" })}
              testid={`adm-news-photo-file-${i}`}
            />
            <input
              type="text"
              maxLength={200}
              value={p.caption || ""}
              onChange={(e) => setAt(i, { caption: e.target.value })}
              placeholder="Caption (optional)"
              data-testid={`adm-news-photo-caption-${i}`}
              className={inputCls}
            />
          </div>
        ))}
        {list.length < NE_MAX_PHOTOS && (
          <button
            type="button"
            onClick={addSlot}
            data-testid="adm-news-photo-add"
            className="border-2 border-dashed border-[#e5e0d8] hover:border-[#234333] hover:bg-[#f5f1e8] rounded-sm min-h-[180px] flex flex-col items-center justify-center gap-2 text-[#6b655b] hover:text-[#234333] transition-colors"
          >
            <ImageIcon size={22} />
            <span className="text-xs uppercase tracking-widest font-semibold">
              Add Photo ({list.length}/{NE_MAX_PHOTOS})
            </span>
            <span className="text-[10px] text-[#8a8478]">Up to 750 KB · JPG / PNG / WEBP</span>
          </button>
        )}
      </div>
      {list.length > 0 && (
        <div className="mt-2 text-[11px] text-[#6b655b]">
          {list.length} of {NE_MAX_PHOTOS} photos attached · use arrows to reorder
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// Important Links — sibling section within News & Events module.
// Managed here, rendered on the Trust public home page.
// -------------------------------------------------------------
function ImportantLinksPanel({ canEdit }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: "", label: "", url: "", order: 0, status: "Active" });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/important-links");
      setLinks(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ id: "", label: "", url: "", order: links.length, status: "Active" }); setOpen(true); };
  const openEdit = (l) => { setForm({ id: l.id, label: l.label || "", url: l.url || "", order: Number(l.order) || 0, status: l.status || "Active" }); setOpen(true); };

  const save = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    const u = (form.url || "").trim();
    if (!/^https?:\/\//i.test(u)) return toast.error("URL must start with http:// or https://");
    const payload = { label: form.label.trim(), url: u, order: Number(form.order) || 0, status: form.status === "Inactive" ? "Inactive" : "Active" };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/important-links/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/important-links", payload);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/important-links/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-white rounded-sm border border-[#e5e0d8] p-5" data-testid="adm-important-links">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h2 className="font-serif text-xl text-[#1c1a17] flex items-center gap-2"><LinkIcon size={18} />Important Links</h2>
          <div className="text-sm text-[#6b655b] mt-0.5">External links shown on the public home page. Each opens in a new tab.</div>
        </div>
        {canEdit && (
          <button type="button" onClick={openAdd} data-testid="adm-imp-links-add" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-3.5 py-2 text-xs font-semibold rounded-sm">
            <Plus size={14} /> Add Link
          </button>
        )}
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="text-sm text-[#6b655b]">Loading…</div>
        ) : links.length === 0 ? (
          <div className="text-sm text-[#6b655b] bg-[#f5f1e8] rounded-sm p-4">No links yet. Add government portals, board sites, admission portals, etc.</div>
        ) : (
          <ul className="divide-y divide-[#f0eadf] border border-[#e5e0d8] rounded-sm">
            {links.map((l) => (
              <li key={l.id} className="flex items-center gap-3 p-3" data-testid={`adm-imp-link-row-${l.id}`}>
                <div className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#234333]/10 text-[#234333] rounded-sm">
                  <ExternalLink size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1c1a17] truncate">{l.label}</div>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#234333] hover:underline break-all">{l.url}</a>
                </div>
                <div className="text-[10px] text-[#6b655b] tracking-wider uppercase font-semibold shrink-0">Order · {l.order}</div>
                {l.status !== "Active" && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-sm font-semibold uppercase tracking-wider shrink-0">Inactive</span>}
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => openEdit(l)} data-testid={`adm-imp-link-edit-${l.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} />Edit</button>
                    <button type="button" onClick={() => setConfirmDelete(l)} data-testid={`adm-imp-link-delete-${l.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm"><Trash2 size={11} />Delete</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{form.id ? "Edit Link" : "Add Important Link"}</DialogTitle>
            <DialogDescription>Shown as a clickable card on the trust home page — opens in a new tab.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <Field label="Label *">
              <input data-testid="adm-imp-link-input-label" type="text" maxLength={160} className={inputCls} value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. AICTE Portal / DTE Maharashtra" />
            </Field>
            <Field label="URL *">
              <input data-testid="adm-imp-link-input-url" type="url" maxLength={800} className={inputCls} value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Display Order">
                <input data-testid="adm-imp-link-input-order" type="number" min="0" className={inputCls} value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} />
              </Field>
              <Field label="Status">
                <select data-testid="adm-imp-link-input-status" className={inputCls} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </Field>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="adm-imp-link-save" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
              {saving ? "Saving…" : (form.id ? "Update" : "Add")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete link?</AlertDialogTitle>
            <AlertDialogDescription>“{confirmDelete?.label}” will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="adm-imp-link-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
